import {
  AlertCircle,
  CalendarClock,
  CircleHelp,
  Medal,
  ShieldCheck,
  Trophy,
} from 'lucide-react';

const scoringRules = [
  {
    points: 10,
    title: 'Placar exato',
    description: 'Acerta os gols dos dois times.',
    example: 'Palpite 2x1, resultado 2x1.',
  },
  {
    points: 7,
    title: 'Saldo e vencedor',
    description: 'Acerta o vencedor e a diferença de gols.',
    example: 'Palpite 3x1, resultado 2x0.',
  },
  {
    points: 3,
    title: 'Vencedor',
    description: 'Acerta quem venceu, sem acertar o saldo.',
    example: 'Palpite 1x0, resultado 3x1.',
  },
  {
    points: 1,
    title: 'Empate',
    description: 'Acerta que o jogo terminou empatado.',
    example: 'Palpite 1x1, resultado 0x0.',
  },
];

const regulations = [
  'Palpites de jogos fecham 15 minutos antes do início da partida.',
  'Palpites especiais fecham na data configurada para cada pergunta.',
  'Resultados oficiais devem ser lançados por administradores com MFA ativo.',
  'O ranking considera apenas palpites ativos e ignora registros removidos.',
  'Bolões privados exigem código de convite; bolões públicos permitem entrada direta.',
];

const faq = [
  {
    question: 'Posso alterar um palpite?',
    answer: 'Sim, enquanto o jogo ou palpite especial ainda não estiver travado.',
  },
  {
    question: 'Como entro em um bolão privado?',
    answer: 'Use o link do bolão e informe o código de convite enviado pelo dono ou administrador.',
  },
  {
    question: 'Quando o ranking muda?',
    answer: 'Depois que um resultado oficial é salvo, o sistema recalcula os rankings afetados.',
  },
  {
    question: 'O que acontece se um jogo for cancelado?',
    answer: 'A partida deixa de pontuar e os rankings relacionados são recalculados.',
  },
];

export default function RulesPage() {
  return (
    <div className="space-y-8">
      <section className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-6 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-primary-600 dark:text-primary-400">Regulamento</p>
            <h1 className="mt-2 text-3xl font-bold text-neutral-900 dark:text-white">
              Regras do Bolão 2026
            </h1>
            <p className="mt-3 max-w-3xl text-neutral-600 dark:text-neutral-400">
              Consulte a pontuação, os prazos de trava e os critérios que controlam o ranking.
            </p>
          </div>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
            <Trophy className="h-7 w-7" />
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-center gap-2">
          <Medal className="h-5 w-5 text-yellow-500" />
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Sistema de pontuação</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {scoringRules.map((rule) => (
            <article
              key={rule.title}
              className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5"
            >
              <div className="mb-4 flex items-baseline gap-2">
                <span className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                  {rule.points}
                </span>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">pontos</span>
              </div>
              <h3 className="font-semibold text-neutral-900 dark:text-white">{rule.title}</h3>
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{rule.description}</p>
              <p className="mt-4 text-xs font-medium text-neutral-500 dark:text-neutral-500">{rule.example}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1fr_0.85fr]">
        <div className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-6">
          <div className="mb-5 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Regulamento</h2>
          </div>
          <ul className="space-y-3">
            {regulations.map((rule) => (
              <li key={rule} className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-500" />
                <span>{rule}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/60 p-6">
          <div className="mb-4 flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-amber-700 dark:text-amber-400" />
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Travas</h2>
          </div>
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Quando uma trava é atingida, o botão de salvar palpite fica indisponível e a API rejeita alterações.
            Essa regra vale para jogos, palpites especiais e fluxos administrativos protegidos.
          </p>
          <div className="mt-5 flex gap-3 rounded-xl bg-white/70 dark:bg-neutral-900/70 p-4">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
            <p className="text-sm text-neutral-700 dark:text-neutral-300">
              Em caso de divergência, prevalece o resultado oficial registrado pelo administrador do bolão.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-center gap-2">
          <CircleHelp className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">FAQ</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faq.map((item) => (
            <article
              key={item.question}
              className="rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5"
            >
              <h3 className="font-semibold text-neutral-900 dark:text-white">{item.question}</h3>
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">{item.answer}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
